import { useRouter } from './router';
import { useEffect, useRef } from 'react';

export type ListFilters = Record<string, string>;

export function changeListFilters(search: string, changes: ListFilters): URLSearchParams {
  const next = new URLSearchParams(search);
  next.delete('cursor');
  next.delete('item');
  Object.entries(changes).forEach(([key, value]) => next.set(key, value));
  return next;
}

/** List state lives in the URL so reload, Back and shared links retain context. */
export function useListState(defaults: ListFilters = {}) {
  const { pathname, search, navigate } = useRouter();
  const params = new URLSearchParams(search);
  const cursors = params.getAll('cursor');
  const id = Number(params.get('item'));
  const selected = Number.isSafeInteger(id) && id > 0 ? id : null;
  const previousSelection = useRef(selected);
  useEffect(() => {
    const previous = previousSelection.current;
    previousSelection.current = selected;
    const frame = requestAnimationFrame(() => {
      if (selected) {
        document.querySelector<HTMLElement>('.record-reader')?.focus({ preventScroll: true });
        // Mobile replaces the results with the reader; reveal it, not the page header.
        if (window.matchMedia('(max-width: 850px)').matches) {
          document.querySelector<HTMLElement>('.records-back')?.scrollIntoView({ block: 'start', behavior: 'auto' });
        }
      }
      else if (previous) document.querySelector<HTMLElement>('[data-record-id="' + previous + '"]')?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [selected]);
  const go = (next: URLSearchParams, preserveScroll = false) => navigate(pathname + (next.size ? '?' + next.toString() : ''), false, { preserveScroll });
  return {
    search, selected, cursor: cursors[cursors.length - 1] || null, page: cursors.length + 1,
    value: (key: string) => params.get(key) ?? defaults[key] ?? '',
    filter: (changes: ListFilters) => go(changeListFilters(search, changes)),
    clear: () => go(new URLSearchParams()),
    select: (value: number | null) => {
      const next = new URLSearchParams(search);
      if (value) next.set('item', String(value)); else next.delete('item');
      go(next, true);
    },
    previous: () => {
      const next = new URLSearchParams(search); next.delete('cursor'); next.delete('item');
      cursors.slice(0, -1).forEach(cursor => next.append('cursor', cursor)); go(next);
    },
    next: (cursor: string) => {
      const next = new URLSearchParams(search); next.delete('item'); next.append('cursor', cursor); go(next);
    }
  };
}

export type ListState = ReturnType<typeof useListState>;
